import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const CustomMarketEvents = () => {
  const [stocks, setStocks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Event form state
  const [eventForm, setEventForm] = useState({
    eventType: 'market', // 'market', 'stock', or 'user'
    stockId: '',
    userId: '',
    description: '',
    multiplier: 0.9, // Default to 10% decrease
    penaltyAmount: 0,
    reason: ''
  });
  
  useEffect(() => {
    fetchStocks();
    fetchUsers();
  }, []);
  
  // Fetch all stocks
  const fetchStocks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stocks`);
      setStocks(response.data);
    } catch (err) {
      console.error('Error fetching stocks:', err);
      setError('Failed to load stocks');
    }
  };
  
  // Fetch users for the dropdown
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/performance`, {
        headers: {
          'x-admin-token': localStorage.getItem('adminKey') || ''
        }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    }
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    // Convert numeric inputs
    const processedValue = type === 'number' ? parseFloat(value) : value;
    
    setEventForm({
      ...eventForm,
      [name]: processedValue
    });
  };
  
  // Handle event type change
  const handleEventTypeChange = (e) => {
    setEventForm({
      ...eventForm,
      eventType: e.target.value,
      // Reset related fields
      stockId: '',
      userId: '',
      multiplier: e.target.value === 'user' ? 1 : 0.9,
      penaltyAmount: 0
    });
  };
  
  // Format multiplier as percentage change
  const formatMultiplierAsPercentage = (multiplier) => {
    const percentChange = (multiplier - 1) * 100;
    const sign = percentChange >= 0 ? '+' : '';
    return `${sign}${percentChange.toFixed(1)}%`;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      if (eventForm.eventType === 'market' || eventForm.eventType === 'stock') {
        // Market or Stock event
        if ((eventForm.eventType === 'stock' && !eventForm.stockId) || !eventForm.description) {
          setError('Please fill in all required fields');
          setLoading(false);
          return;
        }
        
        // Build event data
        const eventData = {
          type: eventForm.eventType === 'market' ? 'MARKET_EVENT' : 'CUSTOM_STOCK_EVENT',
          description: eventForm.description,
          multiplier: eventForm.multiplier,
          affectedStocks: eventForm.eventType === 'market' ? 'ALL' : eventForm.stockId
        };
        
        // Send request with admin authentication
        await axios.post(`${API_BASE_URL}/api/admin/trigger-event`, eventData, {
          headers: {
            'x-admin-token': localStorage.getItem('adminKey') || ''
          }
        });
        
        if (eventForm.eventType === 'market') {
          setSuccessMessage(`Market-wide event triggered: ${formatMultiplierAsPercentage(eventForm.multiplier)} change`);
        } else {
          const stock = stocks.find(s => s.id === eventForm.stockId);
          setSuccessMessage(`Stock event triggered for ${stock?.ticker || 'selected stock'}: ${formatMultiplierAsPercentage(eventForm.multiplier)} change`);
        }
      } else {
        // User event (penalty/bonus)
        if (!eventForm.userId || !eventForm.reason || !eventForm.penaltyAmount) {
          setError('Please select a user, enter a reason, and specify an amount');
          setLoading(false);
          return;
        }
        
        // Create user penalty/bonus request
        const userData = {
          userId: eventForm.userId,
          amount: eventForm.penaltyAmount,
          reason: eventForm.reason
        };
        
        // Send request with admin authentication
        await axios.post(`${API_BASE_URL}/api/admin/user-penalty`, userData, {
          headers: {
            'x-admin-token': localStorage.getItem('adminKey') || ''
          }
        });
        
        const amountType = eventForm.penaltyAmount > 0 ? 'bonus' : 'penalty';
        setSuccessMessage(`User ${amountType} of $${Math.abs(eventForm.penaltyAmount)} applied successfully`);
      }
      
      // Reset form
      setEventForm({
        ...eventForm,
        description: '',
        reason: '',
        penaltyAmount: 0,
        stockId: '',
        multiplier: eventForm.eventType === 'user' ? 1 : 0.9
      });
    } catch (err) {
      console.error('Error triggering custom event:', err);
      setError(err.response?.data?.message || 'Failed to trigger event');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="custom-events-panel">
      <h2>Custom Market Events</h2>
      
      {/* Success message */}
      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage('')}>Dismiss</button>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>Dismiss</button>
        </div>
      )}
      
      <div className="event-type-selector">
        <label>
          <input 
            type="radio" 
            name="eventType" 
            value="market" 
            checked={eventForm.eventType === 'market'} 
            onChange={handleEventTypeChange}
          />
          Market-wide Event
        </label>
        <label>
          <input 
            type="radio" 
            name="eventType" 
            value="stock" 
            checked={eventForm.eventType === 'stock'} 
            onChange={handleEventTypeChange}
          />
          Individual Stock Event
        </label>
        <label>
          <input 
            type="radio" 
            name="eventType" 
            value="user" 
            checked={eventForm.eventType === 'user'} 
            onChange={handleEventTypeChange}
          />
          User Penalty/Bonus
        </label>
      </div>
      
      <form onSubmit={handleSubmit} className="custom-event-form">
        {/* Market-wide event form */}
        {eventForm.eventType === 'market' && (
          <>
            <div className="form-group">
              <label htmlFor="description">Event Description</label>
              <input
                type="text"
                id="description"
                name="description"
                value={eventForm.description}
                onChange={handleInputChange}
                placeholder="e.g., Breaking news causes market panic!"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="multiplier">Price Change (All Stocks)</label>
              <div className="multiplier-input">
                <input
                  type="range"
                  id="multiplier"
                  name="multiplier"
                  min="0.5"
                  max="1.5"
                  step="0.01"
                  value={eventForm.multiplier}
                  onChange={handleInputChange}
                />
                <span className={eventForm.multiplier >= 1 ? 'positive' : 'negative'}>
                  {formatMultiplierAsPercentage(eventForm.multiplier)}
                </span>
              </div>
              <div className="help-text">Drag to set price impact (0.5 = -50%, 1.5 = +50%)</div>
            </div>
            
            <div className="event-preview">
              <h4>Event Preview</h4>
              <p>
                <strong>Affected:</strong> All stocks
              </p>
              <p>
                <strong>Description:</strong> {eventForm.description || 'No description'}
              </p>
              <p>
                <strong>Price Impact:</strong> <span className={eventForm.multiplier >= 1 ? 'positive' : 'negative'}>
                  {formatMultiplierAsPercentage(eventForm.multiplier)}
                </span>
              </p>
            </div>
          </>
        )}
        
        {/* Stock event form */}
        {eventForm.eventType === 'stock' && (
          <>
            <div className="form-group">
              <label htmlFor="stockId">Select Stock</label>
              <select
                id="stockId"
                name="stockId"
                value={eventForm.stockId}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Select a Stock --</option>
                {stocks.map(stock => (
                  <option key={stock.id} value={stock.id}>
                    {stock.ticker} - {stock.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Event Description</label>
              <input
                type="text"
                id="description"
                name="description"
                value={eventForm.description}
                onChange={handleInputChange}
                placeholder="e.g., DOGE CEO resigns amid controversy"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="multiplier">Price Change</label>
              <div className="multiplier-input">
                <input
                  type="range"
                  id="multiplier"
                  name="multiplier"
                  min="0.5"
                  max="1.5"
                  step="0.01"
                  value={eventForm.multiplier}
                  onChange={handleInputChange}
                />
                <span className={eventForm.multiplier >= 1 ? 'positive' : 'negative'}>
                  {formatMultiplierAsPercentage(eventForm.multiplier)}
                </span>
              </div>
              <div className="help-text">Drag to set price impact (0.5 = -50%, 1.5 = +50%)</div>
            </div>
            
            <div className="event-preview">
              <h4>Event Preview</h4>
              <p>
                <strong>Affected Stock:</strong> {stocks.find(s => s.id === eventForm.stockId)?.ticker || 'None selected'}
              </p>
              <p>
                <strong>Description:</strong> {eventForm.description || 'No description'}
              </p>
              <p>
                <strong>Price Impact:</strong> <span className={eventForm.multiplier >= 1 ? 'positive' : 'negative'}>
                  {formatMultiplierAsPercentage(eventForm.multiplier)}
                </span>
              </p>
            </div>
          </>
        )}
        
        {/* User penalty form */}
        {eventForm.eventType === 'user' && (
          <>
            <div className="form-group">
              <label htmlFor="userId">Select User</label>
              <select
                id="userId"
                name="userId"
                value={eventForm.userId}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Select a User --</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.username || user.shortId} (${user.balance.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="reason">Reason</label>
              <input
                type="text"
                id="reason"
                name="reason"
                value={eventForm.reason}
                onChange={handleInputChange}
                placeholder="e.g., SEC investigation into insider trading"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="penaltyAmount">Amount (negative for penalty, positive for bonus)</label>
              <div className="input-with-prefix">
                <span className="input-prefix">$</span>
                <input
                  type="number"
                  id="penaltyAmount"
                  name="penaltyAmount"
                  value={eventForm.penaltyAmount}
                  onChange={handleInputChange}
                  step="100"
                  required
                />
              </div>
              <div className="help-text">
                Enter negative value for penalty (e.g., -500) or positive for bonus (e.g., 500)
              </div>
            </div>
            
            <div className="event-preview">
              <h4>Event Preview</h4>
              <p>
                <strong>User:</strong> {users.find(u => u.id === eventForm.userId)?.username || 'None selected'}
              </p>
              <p>
                <strong>Reason:</strong> {eventForm.reason || 'No reason provided'}
              </p>
              <p>
                <strong>Amount:</strong> <span className={eventForm.penaltyAmount >= 0 ? 'positive' : 'negative'}>
                  ${Math.abs(eventForm.penaltyAmount).toFixed(2)} {eventForm.penaltyAmount >= 0 ? 'bonus' : 'penalty'}
                </span>
              </p>
            </div>
          </>
        )}
        
        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Processing...' : 'Trigger Event'}
        </button>
      </form>
      
      <div className="event-ideas">
        <h3>Event Ideas</h3>
        <div className="event-examples">
          <div className="event-example">
            <strong>Market Crashes:</strong> "Breaking news: Economy in turmoil as major banks struggle" (-25%)
          </div>
          <div className="event-example">
            <strong>Market Rallies:</strong> "Federal Reserve announces new stimulus package" (+15%)
          </div>
          <div className="event-example">
            <strong>Individual Stocks:</strong> "DOGE unveils revolutionary new product" (+40%)
          </div>
          <div className="event-example">
            <strong>User Penalties:</strong> "SEC investigates trader for suspicious trades" (-$5000)
          </div>
          <div className="event-example">
            <strong>User Bonuses:</strong> "Trader receives inheritance from long-lost uncle" (+$10000)
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomMarketEvents;